import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { useTranslations } from "@jugl-web/utils";
import { useCallback, useEffect, useState } from "react";

import { ItemsActionsFloatingBar } from "@jugl-web/ui-components/cross-platform/ItemsActionsFloatingBar";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";
import { ReactComponent as XmlIcon } from "./assets/xml.svg";

import { useInvoicesTableContext } from "../../context/useInvoicesTableContext";
import { InvoicesDeleteConfirmationDialog } from "./components/InvoiceDeleteConfirmationDialog";
import { useExportInvoices } from "./hooks/useExportInvoices";

const InvoicesBulkActions = () => {
  const { selected, handleDeleteInvoice, items, availableActions } =
    useInvoicesTableContext();
  const [isOpen, setIsOpen] = useState(selected.totalSelectedCount > 0);
  const [activeModal, setActiveModal] = useState<"delete" | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { t } = useTranslations();
  const { generateExcel } = useExportInvoices();

  useEffect(() => {
    setIsOpen(selected.totalSelectedCount > 0);
  }, [selected.totalSelectedCount]);

  const handleExport = useCallback(async () => {
    if (!items) return;

    setIsExporting(true);
    try {
      const selectedInvoices = selected.getAllSelectedItems();
      await generateExcel(selectedInvoices);
      selected.unselectAll();
    } catch (error) {
      // pass
    } finally {
      setIsExporting(false);
    }
  }, [items, selected, generateExcel]);

  const sections = [
    {
      label: t({
        defaultMessage: "Export as XLSX",
        id: "invoices.export-as-xlsx",
      }),
      icon: <XmlIcon />,
      onClick: handleExport,
      isDisabled: isExporting,
    },
    ...(availableActions.includes("delete")
      ? [
          {
            label: t({
              defaultMessage: "Delete",
              id: "common.delete",
            }),
            icon: <TrashIcon />,
            onClick: () => {
              setActiveModal("delete");
            },
            isDisabled: isDeleting,
          },
        ]
      : []),
  ];

  const handleBulkDelete = async () => {
    try {
      setIsDeleting(true);
      const selectedIds = selected
        .getAllSelectedItems()
        .map((invoice) => invoice.id);
      await handleDeleteInvoice(selectedIds);
      selected.unselectAll();
      setActiveModal(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  const label =
    selected.totalSelectedCount === 1
      ? t({
          defaultMessage: "document",
          id: "invoices.document",
        })
      : t({
          defaultMessage: "documents",
          id: "invoices.documents",
        });

  return (
    <>
      <ItemsActionsFloatingBar
        label={`${selected.totalSelectedCount} ${label}`}
        onClose={() => {
          setIsOpen(false);
          selected.unselectAll();
        }}
      >
        {sections.map((section) => (
          <InteractiveContainer
            key={section.label}
            className={`hover:bg-grey-100 flex flex-col items-center justify-center px-6 transition-colors ${
              section.isDisabled ? "cursor-not-allowed opacity-50" : ""
            }`}
            onClick={section.isDisabled ? undefined : section.onClick}
          >
            {section.icon}
            <span className="text-dark font-secondary text-sm leading-[150%]">
              {section.label}
            </span>
          </InteractiveContainer>
        ))}
      </ItemsActionsFloatingBar>
      <InvoicesDeleteConfirmationDialog
        isOpen={activeModal === "delete"}
        isDisabled={isDeleting}
        onCancel={() => setActiveModal(null)}
        onRequestClose={() => setActiveModal(null)}
        onConfirm={handleBulkDelete}
      />
    </>
  );
};

export { InvoicesBulkActions };
